import {
  orderTypeLabels,
  PurchaseOrderOrderTypeEnum,
} from "@/features/purchase-orders";
import { useOutletApi } from "@/features/authentication";
import { StatusEnum } from "@/features/sales-requests";
import { formatDate, formatNumber } from "@/features/shared";
import { CustomAsyncAutocomplete, ProductVariantNameCell } from "@/features/ui";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { Button } from "@mui/material";

export const SelectStandardSalesRequestSection = () => {
  const { control } = useFormContext();
  const apiRef = useGridApiRef();
  const [salesRequest, setSalesRequest] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedSalesRequestProductVariants,
    setSelectedSalesRequestProductVariants,
  ] = useState([]);

  const { append, update } = useFieldArray({
    control,
    name: "salesOrderProductVariants",
  });

  const [buyer, seller, salesOrderProductVariants] = useWatch({
    control,
    name: ["buyer", "seller", "salesOrderProductVariants"],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  const disableBrowseSalesRequest = !buyer;

  const fetchUnfinishedSalesRequest = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller) return [];

      const unfinishedStatuses = [
        StatusEnum.PENDING,
        StatusEnum.PARTIALLY_ORDERED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get("sales-requests", {
        params: {
          status: unfinishedStatuses,
          orderType: PurchaseOrderOrderTypeEnum.STANDARD,
          buyerId: buyer?.id,
          keyword: searchValue || null,
          withPurchaseOrder: true,
          page,
          size,
        },
      });
      return data;
    },
    [buyer, seller, outletApi]
  );

  const fetchSalesRequestProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `sales-requests/${referenceNumber}/sales-request-product-variants`,
          {
            params: {
              excludeFullyOrdered: true,
            },
          }
        );

        // 🔥 Adjust remaining quantity based on SOPV
        const adjustedProductVariants = data
          .map((salesRequestProductVariant) => {
            // Calculate total quantity for this variant across all SOPV entries
            const totalSalesOrderQuantity = salesOrderProductVariants
              .filter(
                (salesOrderProductVariant) =>
                  salesOrderProductVariant.salesRequestProductVariantId ===
                  salesRequestProductVariant.id
              )
              .reduce((total, sopv) => total + sopv.quantity, 0);

            return {
              ...salesRequestProductVariant,
              adjustedRemainingQuantity:
                salesRequestProductVariant.purchaseOrderProductVariant
                  .quantity -
                salesRequestProductVariant.orderedQuantity -
                totalSalesOrderQuantity, // Adjust based on total SOPV quantity
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0); // 🚀 Keep only variants with available stock

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [salesOrderProductVariants, seller, outletApi]
  );

  useEffect(() => {
    if (salesRequest) {
      fetchSalesRequestProductVariants(salesRequest.referenceNumber);
    } else {
      setProductVariants([]);
    }
    setSelectedSalesRequestProductVariants([]); // Reset selection when sales request changes
  }, [fetchSalesRequestProductVariants, salesRequest]);

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      valueGetter: (_, row) => row.purchaseOrderProductVariant.name,
      renderCell: ({
        row: {
          purchaseOrderProductVariant: { name, sku },
        },
      }) => <ProductVariantNameCell name={name} sku={sku} />,
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      flex: 1,
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      flex: 1,
      valueGetter: (_, row) =>
        row.purchaseOrderProductVariant.product.unitOfMeasurement.name,
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (_, row) => row.purchaseOrderProductVariant.note || "-",
    },
  ];

  const submitSelection = () => {
    // Reset the state after submission
    setSalesRequest(null);
    setProductVariants([]);

    selectedSalesRequestProductVariants.forEach(
      ({
        id: salesRequestProductVariantId,
        adjustedRemainingQuantity,
        purchaseOrderProductVariant: {
          name,
          sku,
          costPerUnit,
          note,
          product: {
            unitOfMeasurement: { name: unitOfMeasurement },
          },
        },
      }) => {
        // 🔍 Check if the sales order product variant already exists in SOPV
        const existingSalesOrderProductVariantIndex =
          salesOrderProductVariants.findIndex(
            (salesOrderProductVariant) =>
              salesOrderProductVariant.salesRequestProductVariantId ===
              salesRequestProductVariantId
          );

        if (existingSalesOrderProductVariantIndex !== -1) {
          // ✅ Update existing sales order product variant quantity
          update(existingSalesOrderProductVariantIndex, {
            ...salesOrderProductVariants[existingSalesOrderProductVariantIndex],
            quantity:
              salesOrderProductVariants[existingSalesOrderProductVariantIndex]
                .quantity + adjustedRemainingQuantity, // Merge quantities
            costPerUnit, // Update cost per unit as well
          });
        } else {
          // 🔥 Append as new record if not found
          append({
            id: ulid(),
            salesRequestProductVariantId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            maxQuantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            costPerUnit,
            // Carried from the order line; still editable in the grid.
            note: note ?? null,
          });
        }
      }
    );
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={salesRequest}
        onChange={setSalesRequest}
        disabled={disableBrowseSalesRequest}
        fetchOptions={fetchUnfinishedSalesRequest}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const {
            id,
            referenceNumber,
            purchaseOrder: { transactionDate, orderType },
          } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>
                  {referenceNumber} ({orderTypeLabels[orderType]})
                </div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseSalesRequest
            ? "Pilih Pelanggan terlebih dahulu"
            : undefined,
          InputProps: {
            placeholder: "Cari nomor referensi Permintaan Penjualan",
          },
        }}
      />
      <DataGrid
        getRowHeight={() => "auto"}
        apiRef={apiRef}
        columns={columns}
        rows={productVariants}
        loading={loadingProductVariants}
        checkboxSelection
        onRowSelectionModelChange={() => {
          const selectedModels = Array.from(
            gridRowSelectionIdsSelector(apiRef.current.state).values()
          );
          console.log(selectedModels);

          setSelectedSalesRequestProductVariants(selectedModels);
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        // onRowSelectionModelChange={(newSelectionModel) => {
        //   // Handle the new selection model format: {type: 'include', ids: Set(...)}
        //   let selectedIds = [];
        //   if (newSelectionModel && newSelectionModel.ids) {
        //     selectedIds = Array.from(newSelectionModel.ids);
        //   } else if (Array.isArray(newSelectionModel)) {
        //     selectedIds = newSelectionModel;
        //   }

        //   const selectedObjects = productVariants.filter((variant) =>
        //     selectedIds.includes(variant.id)
        //   );
        //   setSelectedSalesRequestProductVariants(selectedObjects);
        // }}
      />
      <Button
        fullWidth
        variant="contained"
        disabled={selectedSalesRequestProductVariants.length === 0}
        sx={{ mt: 1 }}
        onClick={submitSelection}
      >
        Tambah
      </Button>
    </>
  );
};
